import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import clsx from 'clsx';

type TooltipProps = TooltipPrimitive.TooltipProps &
  Omit<TooltipPrimitive.TooltipContentProps, 'sideOffset'>;

function Tooltip({
  children,
  content,
  open,
  defaultOpen,
  onOpenChange,
  delayDuration,
  disableHoverableContent,
  side = 'top',
  align = 'center',
  ...props
}: TooltipProps) {
  return (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          {...props}
          side={side}
          align={align}
          sideOffset={4}
          // Radix's Presence keeps content mounted until a CSS
          // animation/transition it can detect actually completes, so
          // `transition-opacity` must be paired with an opacity value that
          // genuinely changes with `data-state` -- this previously had the
          // transition property with no corresponding value change, so
          // `transitionend` never fired and the tooltip never unmounted
          // once opened. This went uncaught because nothing in the app
          // used this component until now, so no real browser ever
          // exercised the close path (jsdom tests don't run CSS
          // animations at all).
          className={clsx(
            'bg-grey-900 dark:bg-grey-700 z-50 max-w-[20rem] rounded-lg px-3 py-2 text-sm font-medium text-white shadow-xs',
            'transition-opacity duration-150 data-[state=closed]:opacity-0 data-[state=delayed-open]:opacity-100 data-[state=instant-open]:opacity-100',
          )}
        >
          <TooltipPrimitive.Arrow className="dark:text-grey-800 fill-current text-white" />
          <span className="whitespace-pre-line">{content}</span>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

const { TooltipProvider } = TooltipPrimitive;

export { Tooltip, TooltipProvider };
export type { TooltipProps };
